/**
 * Build ids and the update decision (pure; the browser side is
 * `components/BuildUpdate.tsx`).
 *
 * A build id is `<UTC build time, YYYYMMDDHHMMSS>-<tag>` (the tag is the git
 * commit), so ids order by build time (farm.config.ts `buildId`). An id in
 * another shape (an older deploy's bare commit, a hand-set
 * `TERRITILE_BUILD_ID`) compares with nothing: it never triggers an update.
 */

const BUILD_ID = /^(\d{14})-[\w.-]{1,40}$/;

/** The build time part of a build id (comparable as a number), or null. */
export function buildTime(id: string): number | null {
  const m = BUILD_ID.exec(id);
  return m ? Number(m[1]) : null;
}

/** Whether build `a` was built after build `b` (false when either can't be ordered). */
export function isNewerBuild(a: string, b: string): boolean {
  const ta = buildTime(a);
  const tb = buildTime(b);
  return ta !== null && tb !== null && ta > tb;
}

/** The newest of the other peers' builds that is newer than ours, or null. */
export function newestPeerBuild(own: string, peers: readonly (string | null)[]): string | null {
  let newest: string | null = null;
  for (const build of peers) {
    if (build === null || !isNewerBuild(build, own)) continue;
    if (newest === null || isNewerBuild(build, newest)) newest = build;
  }
  return newest;
}

/** `version.json` as the build writes it. */
export function parseVersionFile(data: unknown): string | null {
  if (typeof data !== 'object' || data === null) return null;
  const id = (data as { buildId?: unknown }).buildId;
  return typeof id === 'string' && id.length <= 64 ? id : null;
}

/**
 * Having seen a peer on a newer build: reload when the deployed build (from
 * `version.json`, null when it couldn't be read) is newer than ours and we
 * haven't already reloaded for it this session (`attempted`; reloading again
 * would loop when a cache keeps serving the old build), else say another
 * player is on a different version. Never reloads onto an older build.
 */
export function updateAction(
  own: string,
  deployed: string | null,
  attempted: string | null
): 'reload' | 'notice' {
  if (deployed === null || !isNewerBuild(deployed, own)) return 'notice';
  return deployed === attempted ? 'notice' : 'reload';
}

/**
 * The page to reload onto: the current one, joining `room` again, with the
 * build it's for as `v` (a new URL, so the browser can't answer it from its
 * cache, and the sign to rejoin on load).
 */
export function reloadUrl(href: string, room: string, build: string): string {
  const url = new URL(href);
  url.searchParams.set('room', room);
  url.searchParams.set('v', build);
  return url.toString();
}
